import { useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

/** Насколько близко к краю списка палец включает автопрокрутку. */
const EDGE = 64;
const SCROLL_STEP = 8;

/**
 * Перетаскивание строк списка — порт `AppReorderable` ([D-37](docs/DECISIONS.md)).
 *
 * Три вещи, на которых разбилась первая реализация на Android, повторены здесь
 * намеренно:
 *
 *  1. **Перестановка по идентификаторам, а не по номерам строк.** «Поставить документ A
 *     на место документа B» верно при любом фильтре и любой группировке; номер строки
 *     врёт, как только список отфильтрован.
 *  2. **Порядок пишется целиком.** Наверх уходит готовый список идентификаторов —
 *     арифметика «середина между соседями» схлопывается с первого же переноса.
 *  3. **Автопрокрутка у краёв.** Без неё в деле на полсотни документов строку некуда
 *     дотянуть.
 *
 * Пока строку тянут, список не прокручивается пальцем: два жеста на одной поверхности
 * конкурируют, и система не угадает намерение.
 */
export function AppReorderable<T>({
  items,
  keyOf,
  onReorder,
  renderRow,
}: {
  items: T[];
  keyOf: (item: T) => string;
  /** Готовый порядок целиком — экрану остаётся только записать его. */
  onReorder: (orderedIds: string[]) => void;
  renderRow: (item: T, handle: ReactNode, dragged: boolean) => ReactNode;
}) {
  const [dragged, setDragged] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const order = useRef<string[]>([]);

  const idsNow = () => items.map(keyOf);

  /** Строка под пальцем: ищем по реальным границам, а не по индексу. */
  const rowAt = (y: number): string | null => {
    const rows = list.current?.querySelectorAll<HTMLElement>('[data-reorder-key]');
    if (!rows) return null;

    for (const row of rows) {
      const rect = row.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) return row.dataset.reorderKey ?? null;
    }
    return null;
  };

  const autoScroll = (y: number) => {
    const scroller = list.current?.closest<HTMLElement>('.screen__content');
    if (!scroller) return;

    const rect = scroller.getBoundingClientRect();
    if (y - rect.top < EDGE) scroller.scrollBy(0, -SCROLL_STEP);
    else if (rect.bottom - y < EDGE) scroller.scrollBy(0, SCROLL_STEP);
  };

  const start = (id: string) => (event: React.PointerEvent) => {
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture(event.pointerId);
    order.current = idsNow();
    setDragged(id);
  };

  const move = (event: React.PointerEvent) => {
    if (!dragged) return;

    autoScroll(event.clientY);

    const over = rowAt(event.clientY);
    if (!over || over === dragged) return;

    const next = [...order.current];
    const from = next.indexOf(dragged);
    const to = next.indexOf(over);
    if (from < 0 || to < 0) return;

    next.splice(to, 0, ...next.splice(from, 1));
    order.current = next;
    onReorder(next);
  };

  const finish = () => setDragged(null);

  return (
    <div ref={list} onPointerMove={move} onPointerUp={finish} onPointerCancel={finish}>
      {items.map((item) => {
        const id = keyOf(item);
        const handle = (
          <span
            className="reorderable__handle"
            role="button"
            aria-label="Переместить"
            onPointerDown={start(id)}
          >
            <Icon name="drag_indicator" size={22} />
          </span>
        );

        return (
          <div
            key={id}
            data-reorder-key={id}
            className={`reorderable__row ${dragged === id ? 'reorderable__row--dragged' : ''}`}
          >
            {renderRow(item, handle, dragged === id)}
          </div>
        );
      })}
    </div>
  );
}
